import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowSquareOut, CheckCircle, GlobeHemisphereWest,
  MagnifyingGlass, Phone, SealCheck, WarningCircle,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const fieldClass =
  "h-11 border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#C5A059]";

const titleCase = (value) =>
  String(value || "—")
    .replaceAll("_", " ")
    .replace(/\\b\\w/g, (letter) => letter.toUpperCase());

function CandidateCard({ candidate, importing, onImport, t }) {
  const [email, setEmail] = useState(candidate.contact_email || "");
  const [phone, setPhone] = useState(candidate.contact_phone || "");

  const ready = Boolean(email.trim() && phone.trim());

  return (
    <article className="flex h-full flex-col border border-slate-200 bg-white">
      <div className={`h-1 ${ready ? "bg-emerald-500" : "bg-[#C5A059]"}`} />
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#A77E35]">
              {candidate.website_domain}
            </p>
            <h2 className="mt-1 font-serif text-xl leading-tight text-[#0B2545]">
              {candidate.name}
            </h2>
          </div>
          <span className={`border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${
            ready
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-amber-200 bg-amber-50 text-amber-700"
          }`}>
            {ready
              ? t("investorOwnership.discovery.contactReady", "Contact ready")
              : t("investorOwnership.discovery.needsContact", "Needs contact")}
          </span>
        </div>

        <p className="mt-4 line-clamp-4 text-sm leading-6 text-slate-600">
          {candidate.description ||
            t("investorOwnership.noDescription", "No profile description recorded.")}
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          {(candidate.profile_types || []).map((item) => (
            <span key={item} className="bg-slate-50 px-2 py-1 text-xs text-slate-600">
              {titleCase(item)}
            </span>
          ))}
          {(candidate.investment_sectors || []).slice(0, 3).map((item) => (
            <span key={item} className="bg-[#C5A059]/10 px-2 py-1 text-xs text-[#8A692D]">
              {titleCase(item)}
            </span>
          ))}
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-slate-400">
              {t("investorOwnership.discovery.email", "Reviewed email")}
            </label>
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="commercial@profile.com"
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-slate-400">
              {t("investorOwnership.discovery.phone", "Reviewed telephone")}
            </label>
            <Input
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="+351"
            />
          </div>
        </div>

        <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-5">
          <a
            href={candidate.source_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#0B2545]"
          >
            <ArrowSquareOut size={15} />
            {t("investorOwnership.discovery.reviewSource", "Review source")}
          </a>

          <Button
            type="button"
            disabled={!ready || candidate.already_imported || importing}
            onClick={() =>
              onImport({
                ...candidate,
                contact_email: email.trim(),
                contact_phone: phone.trim(),
                contact_verification_status: "probable",
              })
            }
            className="rounded-none bg-[#0B2545] hover:bg-[#12345e]"
          >
            {candidate.already_imported
              ? t("investorOwnership.discovery.imported", "Already imported")
              : importing
                ? t("investorOwnership.discovery.importing", "Importing…")
                : t("investorOwnership.discovery.import", "Import profile")}
          </Button>
        </div>
      </div>
    </article>
  );
}

export default function InvestorOwnershipDiscovery() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [country, setCountry] = useState("Portugal");
  const [profileType, setProfileType] = useState("");
  const [sector, setSector] = useState("");
  const [strategy, setStrategy] = useState("");
  const [officialUrl, setOfficialUrl] = useState("");

  const { data: meta } = useQuery({
    queryKey: ["investor-ownership-meta"],
    queryFn: async () =>
      (await api.get("/investor-ownership/meta")).data,
  });

  const discovery = useMutation({
    mutationFn: async (payload) =>
      (await api.post("/investor-ownership/web/discover", payload)).data,
    onError: (error) => {
      toast.error(
        error?.response?.data?.detail ||
          t("investorOwnership.discovery.searchError", "Profile discovery failed.")
      );
    },
  });

  const importMutation = useMutation({
    mutationFn: async (candidate) =>
      (await api.post("/investor-ownership/web/import", { candidate })).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["investor-ownership"],
      });
      toast.success(
        t("investorOwnership.discovery.importSuccess", "Profile imported for review.")
      );
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.detail ||
          t("investorOwnership.discovery.importError", "Profile could not be imported.")
      );
    },
  });

  const submit = (event) => {
    event.preventDefault();
    discovery.mutate({
      search_term: searchTerm,
      country,
      profile_type: profileType,
      sector,
      strategy,
      language: i18n.resolvedLanguage || i18n.language || "en",
      official_url: officialUrl,
    });
  };

  const results = discovery.data?.results || [];

  return (
    <div className="space-y-8 pb-12">
      <header>
        <button
          type="button"
          onClick={() => navigate("/investor-ownership")}
          className="mb-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-[#0B2545]"
        >
          <ArrowLeft size={16} />
          {t("investorOwnership.discovery.back", "Back to Investor & Ownership Intelligence")}
        </button>
        <p className="text-xs uppercase tracking-[0.28em] text-[#A77E35]">
          {t("investorOwnership.discovery.eyebrow", "Controlled public-source research")}
        </p>
        <h1 className="mt-2 font-serif text-4xl text-[#0B2545] md:text-5xl">
          {t("investorOwnership.discovery.title", "Discover Investor & Ownership Profiles")}
        </h1>
        <p className="mt-3 max-w-3xl text-base leading-7 text-slate-500">
          {t(
            "investorOwnership.discovery.subtitle",
            "Research official profile sources, review professional contact details and import only complete records."
          )}
        </p>
      </header>

      <form onSubmit={submit} className="border border-slate-200 bg-white p-5">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <label className="relative">
            <MagnifyingGlass
              size={19}
              className="absolute left-4 top-3 text-slate-400"
            />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t(
                "investorOwnership.discovery.searchPlaceholder",
                "Investor, organisation or ownership signal"
              )}
              className={`${fieldClass} w-full pl-11`}
            />
          </label>

          <Input
            required
            value={country}
            onChange={(event) => setCountry(event.target.value)}
            placeholder={t("investorOwnership.discovery.country", "Country")}
          />

          <select
            value={profileType}
            onChange={(event) => setProfileType(event.target.value)}
            className={fieldClass}
            aria-label={t(
              "investorOwnership.discovery.profileType",
              "Investor profile type"
            )}
          >
            <option value="">
              {t(
                "investorOwnership.discovery.allProfileTypes",
                "All investor profile types"
              )}
            </option>
            {(meta?.profile_types || []).map((item) => (
              <option key={item} value={item}>
                {titleCase(item)}
              </option>
            ))}
          </select>

          <select
            value={sector}
            onChange={(event) => setSector(event.target.value)}
            className={fieldClass}
          >
            <option value="">
              {t("investorOwnership.allSectors", "All investment sectors")}
            </option>
            {(meta?.investment_sectors || []).map((item) => (
              <option key={item} value={item}>
                {titleCase(item)}
              </option>
            ))}
          </select>

          <select
            value={strategy}
            onChange={(event) => setStrategy(event.target.value)}
            className={fieldClass}
            aria-label={t(
              "investorOwnership.discovery.strategy",
              "Investment strategy"
            )}
          >
            <option value="">
              {t(
                "investorOwnership.discovery.allStrategies",
                "All investment strategies"
              )}
            </option>
            {(meta?.strategies || []).map((item) => (
              <option key={item} value={item}>
                {titleCase(item)}
              </option>
            ))}
          </select>

          <Input
            type="url"
            value={officialUrl}
            onChange={(event) => setOfficialUrl(event.target.value)}
            placeholder={t(
              "investorOwnership.discovery.officialUrl",
              "Known official website (optional)"
            )}
          />

          <Button
            type="submit"
            disabled={discovery.isPending}
            className="h-11 rounded-none bg-[#0B2545] px-6 hover:bg-[#12345e] xl:col-span-4 xl:w-auto xl:justify-self-end"
          >
            <GlobeHemisphereWest size={18} className="mr-2" />
            {discovery.isPending
              ? t("investorOwnership.discovery.searching", "Searching…")
              : t("investorOwnership.discovery.search", "Search sources")}
          </Button>
        </div>
      </form>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="border border-slate-200 bg-white p-5">
          <SealCheck size={24} className="text-[#0B2545]" />
          <p className="mt-3 text-sm text-slate-600">
            {t(
              "investorOwnership.discovery.ruleOfficial",
              "Review the official profile website before importing."
            )}
          </p>
        </div>
        <div className="border border-slate-200 bg-white p-5">
          <Phone size={24} className="text-[#C5A059]" />
          <p className="mt-3 text-sm text-slate-600">
            {t(
              "investorOwnership.discovery.ruleContact",
              "Email and telephone are mandatory for every imported profile."
            )}
          </p>
        </div>
        <div className="border border-slate-200 bg-white p-5">
          <WarningCircle size={24} className="text-amber-600" />
          <p className="mt-3 text-sm text-slate-600">
            {t(
              "investorOwnership.discovery.ruleReview",
              "Discovered information remains unverified until reviewed."
            )}
          </p>
        </div>
      </div>

      {discovery.data && (
        <div className="flex items-center justify-between border-l-2 border-[#C5A059] bg-slate-100 px-5 py-4">
          <span className="text-sm text-[#0B2545]">
            {discovery.data.count}{" "}
            {t("investorOwnership.discovery.results", "candidate profiles found")}
          </span>
          <span className="text-xs text-slate-500">
            {t("investorOwnership.discovery.provider", "Provider")}:{" "}
            {discovery.data.provider || "—"}
          </span>
        </div>
      )}

      {results.length > 0 ? (
        <section className="grid items-stretch gap-5 lg:grid-cols-2">
          {results.map((candidate) => (
            <CandidateCard
              key={candidate.candidate_id}
              candidate={candidate}
              importing={importMutation.isPending}
              onImport={(record) => importMutation.mutate(record)}
              t={t}
            />
          ))}
        </section>
      ) : discovery.data ? (
        <div className="border border-dashed border-slate-300 bg-white py-16 text-center">
          <CheckCircle size={34} className="mx-auto text-[#C5A059]" />
          <p className="mt-4 text-slate-500">
            {t(
              "investorOwnership.discovery.noResults",
              "No suitable official profile candidates were found."
            )}
          </p>
        </div>
      ) : null}
    </div>
  );
}
